import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import useChatbot from "../../hooks/useChatbot";
import "./chatbot.css";

function ChatIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.8L3 20l1.2-3.6C3.5 15.1 3 13.6 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  );
}

function TypingIndicator() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="chatbot-bubble chatbot-bubble--bot"
      aria-label="Assistant is typing"
    >
      <span className="chatbot-typing">
        <span />
        <span />
        <span />
      </span>
    </motion.div>
  );
}

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const {
    settings,
    settingsLoading,
    settingsError,
    messages,
    input,
    setInput,
    typing,
    sending,
    sendMessage,
    handleQuickReply,
    loadSettings,
  } = useChatbot();

  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, messages, typing]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const lastQuickReplies = [...messages].reverse().find((m) => m.role === "bot" && m.quickReplies?.length)
    ?.quickReplies;

  return (
    <motion.div className="chatbot-root" initial={false}>
      <AnimatePresence>
        {open ? (
          <motion.div
            key="panel"
            className="chatbot-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            role="dialog"
            aria-label="Chat assistant"
          >
            <header className="chatbot-header">
              <div className="chatbot-header__brand">
                <span className="chatbot-header__dot" aria-hidden />
                <div>
                  <p className="chatbot-header__title">
                    {settings?.companyName ? `${settings.companyName} Assistant` : "Inception Assistant"}
                  </p>
                  <p className="chatbot-header__status">
                    {settingsLoading ? "Connecting…" : "Online · Enquiry support"}
                  </p>
                </div>
                </div>
              <button
                type="button"
                className="chatbot-header__close"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
              >
                ✕
              </button>
            </header>

            <div ref={listRef} className="chatbot-messages">
              {settingsError ? (
                <div className="chatbot-error">
                  <p>{settingsError}</p>
                  <button type="button" onClick={loadSettings}>
                    Retry
                  </button>
                </div>
              ) : null}

              {settingsLoading && messages.length === 0 ? (
                <p className="chatbot-loading">Loading assistant…</p>
              ) : null}

              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`chatbot-row chatbot-row--${msg.role}`}
                >
                  <div
                    className={`chatbot-bubble ${
                      msg.role === "user" ? "chatbot-bubble--user" : "chatbot-bubble--bot"
                    }`}
                  >
                    {msg.text.split("\n").map((line, i) => (
                      <span key={i}>
                        {line}
                        {i < msg.text.split("\n").length - 1 ? <br /> : null}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}

              {typing ? (
                <div className="chatbot-row chatbot-row--bot">
                  <TypingIndicator />
                </div>
              ) : null}
            </div>

            {lastQuickReplies?.length && !typing && !sending ? (
              <div className="chatbot-quick-replies">
                {lastQuickReplies.map((label) => (
                  <button
                    key={label}
                    type="button"
                    className="chatbot-quick-reply"
                    onClick={() => handleQuickReply(label)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}

            <form className="chatbot-form" onSubmit={onSubmit}>
              <input
                ref={inputRef}
                type="text"
                className="chatbot-input"
                placeholder="Type your message…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={sending || settingsLoading}
                autoComplete="off"
                aria-label="Message"
              />
              <button
                type="submit"
                className="chatbot-send"
                disabled={!input.trim() || sending || settingsLoading}
                aria-label="Send message"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M3.4 20.4l17.45-7.48a1 1 0 000-1.84L3.4 3.96a1 1 0 00-1.52 1.05l2.09 7.18a1 1 0 00.95.73H11v2H4.92a1 1 0 00-.95.73l-2.09 7.18a1 1 0 001.52 1.05z" />
                </svg>
              </button>
            </form>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <motion.button
        type="button"
        className="chatbot-fab"
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-expanded={open}
        aria-label={open ? "Close chat" : "Open chat assistant"}
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              className="text-xl font-light"
            >
              ✕
            </motion.span>
          ) : (
            <motion.span key="open" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.6, opacity: 0 }}>
              <ChatIcon />
            </motion.span>
          )}
        </AnimatePresence>
        {!open ? <span className="chatbot-fab__pulse" aria-hidden /> : null}
      </motion.button>
    </motion.div>
  );
}
